import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ShortFilmCard from "@/components/feature/ShortFilmCard";
import NewsListItem from "@/components/feature/NewsListItem";
import EmptyState from "@/components/base/EmptyState";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { searchFilms, searchNews, sortFilms } from "@/lib/content";
import { shortFilms } from "@/mocks/shortFilms";

const SUGGESTIONS = ["归途", "动画", "科幻", "短片节", "纪录片", "片单"];
const PAGE_STEP = 6;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get("q") ?? "").trim();
  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState<"film" | "news">("film");
  const [visible, setVisible] = useState(PAGE_STEP);

  const films = searchFilms(keyword);
  const news = searchNews(keyword);
  const total = films.length + news.length;

  useDocumentMeta(
    keyword ? `“${keyword}” 搜索结果 - 小电影` : "搜索 - 小电影影视短片与资讯",
    keyword
      ? `在小电影中搜索“${keyword}”，共找到 ${total} 条相关内容，包含 ${films.length} 部短片与 ${news.length} 条资讯。`
      : "搜索小电影站内的影视短片与影视资讯，输入片名、导演、类型或关键词即可。",
    "小电影搜索,影视短片搜索,影视资讯搜索",
  );

  useEffect(() => {
    setInput(keyword);
    const hasFilms = searchFilms(keyword).length > 0;
    setTab(hasFilms ? "film" : "news");
    setVisible(PAGE_STEP);
  }, [keyword]);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    if (!value) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q: value });
  };

  const runSuggestion = (word: string) => {
    setInput(word);
    setSearchParams({ q: word });
  };

  const shownFilms = films.slice(0, visible);
  const shownNews = news.slice(0, visible);
  const recommended = sortFilms(shortFilms, "popular").slice(0, 4);

  return (
    <PageShell>
      <section className="bg-background-100/70 border-b border-background-200">
        <div className="container-page py-6 md:py-8">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
          <h1 className="mt-3 text-2xl md:text-3xl font-extrabold tracking-tight text-foreground-950">
            站内搜索
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            输入片名、导演、类型或关键词，一次找到相关短片与影视资讯。
          </p>

          <form onSubmit={submitSearch} className="mt-5 flex flex-col sm:flex-row gap-2.5 max-w-2xl">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                type="search"
                placeholder="例如：归途、动画、短片节"
                aria-label="搜索关键词"
                className="w-full h-11 pl-10 pr-3 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
              />
            </div>
            <button
              type="submit"
              className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-foreground-500">试试：</span>
            {SUGGESTIONS.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => runSuggestion(word)}
                className="h-6 px-2.5 rounded-full bg-background-50 border border-background-200 text-[11px] text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="container-page py-7 md:py-9">
        {!keyword ? (
          <div className="max-w-3xl mx-auto">
            <EmptyState
              title="还没有输入关键词"
              description="你可以搜索短片标题、导演、类型标签，也可以搜索资讯标题与正文内容。"
              icon="ri-search-line"
            />
            <div className="mt-8">
              <h2 className="text-base font-bold text-foreground-950 mb-4">大家都在看</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {recommended.map((film) => (
                  <ShortFilmCard key={film.id} film={film} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-7">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-background-200">
                <p className="text-sm text-foreground-600">
                  关键词 <span className="font-semibold text-foreground-950">“{keyword}”</span> 共找到{" "}
                  <span className="font-label font-bold text-primary-600">{total}</span> 条结果
                </p>
                <div className="flex items-center gap-1 p-1 rounded-full bg-background-100">
                  <button
                    type="button"
                    onClick={() => setTab("film")}
                    className={`h-7 px-3 rounded-full text-[12px] transition-colors cursor-pointer whitespace-nowrap ${
                      tab === "film"
                        ? "bg-background-50 text-primary-600 font-medium"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    短片 · {films.length}
                  </button>
                  <button
                    type="button"
                    onClick={() => setTab("news")}
                    className={`h-7 px-3 rounded-full text-[12px] transition-colors cursor-pointer whitespace-nowrap ${
                      tab === "news"
                        ? "bg-background-50 text-primary-600 font-medium"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    资讯 · {news.length}
                  </button>
                </div>
              </div>

              {tab === "film" ? (
                films.length > 0 ? (
                  <>
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">
                      {shownFilms.map((film) => (
                        <ShortFilmCard key={film.id} film={film} />
                      ))}
                    </div>
                    {visible < films.length && (
                      <div className="mt-7 text-center">
                        <button
                          type="button"
                          onClick={() => setVisible((prev) => Math.min(prev + PAGE_STEP, films.length))}
                          className="inline-flex items-center gap-2 h-10 px-6 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-add-line" />
                          加载更多短片
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="mt-5">
                    <EmptyState
                      title="没有匹配的短片"
                      description={`暂时没有和“${keyword}”相关的短片，换个关键词，或看看资讯里有没有提到它。`}
                      actionLabel="切换到资讯结果"
                      onAction={() => setTab("news")}
                      icon="ri-film-line"
                    />
                  </div>
                )
              ) : news.length > 0 ? (
                <>
                  <div className="mt-4 rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
                    {shownNews.map((item) => (
                      <NewsListItem key={item.id} item={item} />
                    ))}
                  </div>
                  {visible < news.length && (
                    <div className="mt-7 text-center">
                      <button
                        type="button"
                        onClick={() => setVisible((prev) => Math.min(prev + PAGE_STEP, news.length))}
                        className="inline-flex items-center gap-2 h-10 px-6 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-add-line" />
                        加载更多资讯
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-5">
                  <EmptyState
                    title="没有匹配的资讯"
                    description={`暂时没有和“${keyword}”相关的资讯。可以换个说法，或者浏览全部影视资讯。`}
                    actionLabel="查看全部资讯"
                    onAction={() => setTab("film")}
                    icon="ri-newspaper-line"
                  />
                </div>
              )}

              {total === 0 && (
                <div className="mt-8">
                  <h2 className="text-base font-bold text-foreground-950 mb-2">也许你会喜欢</h2>
                  <p className="text-[13px] text-foreground-600 mb-4">
                    换个关键词试试，或者从下面的热门短片开始。
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {recommended.map((film) => (
                      <ShortFilmCard key={film.id} film={film} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <aside className="lg:w-[280px] shrink-0">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="text-sm font-bold text-foreground-950 mb-3">搜索小贴士</h2>
                <ul className="space-y-2.5 text-[12px] leading-relaxed text-foreground-600">
                  <li className="flex gap-2">
                    <i className="ri-check-line text-primary-500 mt-0.5" />
                    支持搜索短片标题、简介、导演与标签
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-check-line text-primary-500 mt-0.5" />
                    资讯会匹配标题、摘要与正文内容
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-check-line text-primary-500 mt-0.5" />
                    关键词越长，结果越精确
                  </li>
                </ul>
                <div className="mt-4 pt-4 border-t border-background-200">
                  <Link
                    to="/category/shortfilms"
                    className="inline-flex items-center gap-1.5 text-[12px] font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
                  >
                    按分类浏览短片
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        )}
      </div>
    </PageShell>
  );
}